/*
 * FleetMapView — the fleet map screen.
 *
 * Three states, all of them normal:
 *   - vehicles with a position     -> markers on the map
 *   - vehicles without one         -> listed beside it with a reason
 *   - no credential at all         -> the empty state, NOT a broken map
 *
 * The last one matters more than it looks. The backend withholds the HERE map key from a
 * user whose organisations have no enabled connection, so removing the last credential
 * removes the map itself. A customer who has never added a credential is in exactly that
 * state too, so this is the first screen they ever see, not an error path.
 */

import React from 'react';
import { translate } from 'react-i18next';

import CBMediaQuery from '../shared/CBMediaQuery';
import { getCsrfToken, getStyle } from '../../utils/utils';
import { fleetLocationStyles as styles } from './fleetLocationStyles';

import {
  labelFor, pinLabel, mapViewFor, shouldReframe, stateLabel, isCurrent,
  cardFieldsFor, Generations, FLEET_POSITIONS_URL, UNRECONCILED_URL,
} from './fleetMapCore';
import CBMobileCard from '../shared/CBMobileCard';
import { loadHereSdk } from './hereSdk';
import InlineNameEditor from './InlineNameEditor';

const CONFIG_URL = '/uiapi/1/fleetlocation/config/';
const VEHICLES_URL = '/uiapi/1/fleetlocation/vehicles/';

const PIN_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="34" viewBox="0 0 24 34">'
  + '<path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 22 12 22s12-13 12-22c0-6.6-5.4-12-12-12z" '
  + 'fill="FILL" stroke="STROKE" stroke-width="1.5"/>'
  + '<circle cx="12" cy="12" r="4.5" fill="#f5f5f5"/></svg>';

/*
 * A PIN CARRIES ITS OWN NAME. Twelve identical teal drops tell a reader that twelve trucks
 * exist and nothing about which is which, so checking the map against the table meant counting
 * pins and guessing. The name is rendered as markup rather than drawn into the icon because
 * the browser knows how wide text is and we do not: an SVG label sized by a
 * characters-times-pixels guess clips the first name that disagrees with the guess.
 */
function pinMarkup(label, stale, unreconciled) {
  // A THIRD STATE NEEDS A THIRD COLOUR. These trucks are reported by the customer's own
  // credentials but are not in their Ovio fleet, and drawing them like fleet vehicles would
  // claim a record that does not exist. Amber says "yours to add", not "yours".
  const svg = unreconciled
    ? PIN_SVG.replace('FILL', '#ef6c00').replace('STROKE', '#b53d00')
    : stale
    ? PIN_SVG.replace('FILL', '#9e9e9e').replace('STROKE', '#616161')
    : PIN_SVG.replace('FILL', '#00838f').replace('STROKE', '#005662');
  const text = String(label).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return (
    '<div style="position:relative;width:0;height:0">'
    + '<div style="position:absolute;left:-12px;top:-34px">' + svg + '</div>'
    + '<div style="position:absolute;left:14px;top:-30px;white-space:nowrap;'
    + 'font:600 12px/16px Arial,Helvetica,sans-serif;color:#212121;background:rgba(255,255,255,0.92);'
    + 'border:1px solid ' + (unreconciled ? '#ef6c00' : stale ? '#9e9e9e' : '#00838f')
    + ';border-radius:3px;padding:1px 5px">'
    + text + '</div></div>'
  );
}


function getJson(url) {
  return fetch(url, { credentials: 'include', headers: { Accept: 'application/json' } }).then(
    (resp) => {
      if (!resp.ok) {
        const error = new Error('http ' + resp.status);
        error.status = resp.status;
        throw error;
      }
      return resp.json();
    }
  );
}

class FleetMapView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      status: 'loading',
      hasKey: false,
      mapConfigured: false,
      entries: [],      // { vehicle, provisioned, position, stale, state }
      // A SEPARATE SLICE, deliberately. These are not fleet vehicles: they have no Ovio
      // record, no id and no permissions of their own, and the map's contract for `entries`
      // is pinned by tests on both sides of the wire. Mixing them in would break that and
      // would also make a truck nobody owns look like one that is owned.
      unreconciled: [],
      message: '',
    };
    this.mapRef = React.createRef();
    this.map = null;
    this.markers = {};
    this.framedBox = null;
    this.generations = new Generations();
    // The tail of the refresh chain. Loads queue behind it rather than racing it.
    this.pending = null;
  }

  clearMarkers() {
    if (this.group) {
      this.group.removeAll();
    }
    this.markers = {};
    // The framed extent belongs to the markers that are gone. Keeping it would make the
    // first pin of a reloaded fleet look 'already on screen' and skip the only frame that
    // matters.
    this.framedBox = null;
  }

  componentDidMount() {
    this.mounted = true;
    this.load();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.organizationId !== this.props.organizationId) {
      this.load();
      this.clearMarkers();
      this.safeSetState({ status: 'loading', entries: [], unreconciled: [] });
    }
  }

  componentWillUnmount() {
    // THE MAP OUTLIVES THE COMPONENT OTHERWISE. Each mount builds a full HERE map with its
    // own behaviour, UI and marker group, all holding a canvas and window-level listeners.
    // Navigating to a vehicle and back leaks one of each, and a fleet manager does that all
    // day. Five other screens in this product clean up on unmount; these two did not.
    this.mounted = false;
    if (this.map) {
      this.map.dispose();
      this.map = null;
      this.group = null;
    }
    this.markers = {};
  }

  /* Writing state after unmount is a React warning and a wasted render. Position reads are
     in flight for as long as the provider takes, so a user who leaves mid-load lands here. */
  safeSetState(next, callback) {
    if (this.mounted) {
      this.setState(next, callback);
    }
  }

  /**
   * Refresh, never overlapping.
   *
   * The generation counter stops a superseded load WRITING state, but not from having
   * requests already in flight: two overlapping loads each ran their own two-wide queue, so
   * an administrator clicking add then remove could put four requests against a pool of
   * four. Serialising here means the bound is a property of the component, not of one load.
   */
  load() {
    // THE GENERATION IS CLAIMED HERE, when the refresh is REQUESTED. Claiming it inside
    // `_load` meant a queued refresh could not supersede the load it was queued behind: that
    // load ran to completion against a fleet already replaced, holding a server worker per
    // vehicle, before this one started.
    const generation = this.generations.claim();
    this.pending = Promise.resolve(this.pending)
      .catch(() => {})
      .then(() => this._load(generation));
    return this.pending;
  }

  async _load(generation) {
    // ONE LOAD AT A TIME, and the newest wins. Adding or removing a credential triggers a
    // refresh, so an impatient administrator can start several; each would run its own
    // position queue, and two queues of two is four concurrent requests against a pool of
    // four. The generation lets a superseded load notice it has been overtaken and stop --
    // including before it starts, when it was queued behind a load that is now stale.
    const current = () => this.generations.isCurrent(generation);
    if (!current()) {
      return;   // superseded while queued; the newer load will do this work
    }

    try {
      const config = await getJson(CONFIG_URL);
      if (!current()) {
        return;
      }
      if (!config.fleetLocationEnabled) {
        this.safeSetState({ status: 'disabled', message: this.props.t('notAvailable') });
        return;
      }
      const rows = await getJson(VEHICLES_URL);
      if (!current()) {
        return;
      }
      const entries = rows.map((row) => ({
        vehicle: row.vehicle,
        provisioned: row.provisioned,
        position: null,
        stale: false,
        state: row.provisioned ? 'pending' : 'not_connected',
      }));

      // EVERY MARKER GOES, because the fleet has just been re-read. A vehicle whose
      // credential was removed keeps its pin otherwise: the table says uncovered while the
      // map still shows it, which is the map lying about the more visible of the two.
      this.clearMarkers();

      // THE MAP KEY IS SEPARATE FROM THE FLEET. Without a credential the backend withholds
      // it, and the honest screen is the fleet listed as unconnected rather than an empty
      // grey rectangle where a map should be.
      const hasKey = !!config.hereApiKey;
      // TWO CAUSES, TWO MESSAGES. No key because the user holds no credential is a
      // different sentence from no key because the environment was deployed without
      // one, and telling a correctly-provisioned customer to add a credential sends
      // them to fix something that was never broken.
      this.safeSetState({
        status: 'ready', hasKey, mapConfigured: !!config.mapConfigured, entries,
      });
      if (hasKey) {
        // AWAITED. The map SDK loads over the network; starting position fetches alongside
        // it is a race, and the loser is silent — a marker drawn before the map exists is
        // dropped, so the table shows a position the map does not. It won often enough in
        // testing to look fine, which is the worst frequency for a race to have.
        await this.initMap(config.hereApiKey);
      }
      if (!current()) {
        return;
      }
      await this.loadFleetPositions(current);
      await this.loadUnreconciled(current);
    } catch (err) {
      this.safeSetState({ status: 'error', message: this.props.t('couldNotLoadFleet') });
    }
  }

  async saveName(id, friendlyName, signal) {
    const generation = this.generations.latest;
    const response = await fetch('/uiapi/1/fleetlocation/vehicle/' + id + '/name/', {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getCsrfToken() },
      body: JSON.stringify({ friendly_name: friendlyName }),
      ...(signal ? { signal } : {}),
    });
    if (!response.ok) { throw new Error('nameSaveFailed'); }
    const data = await response.json();
    if (!data.vehicle || data.vehicle.id !== id) { throw new Error('nameSaveFailed'); }
    if (!this.mounted || !this.generations.isCurrent(generation) || (signal && signal.aborted)) { return; }
    this.setState((prev) => ({
      entries: prev.entries.map((entry) => entry.vehicle.id === id ? { ...entry, vehicle: data.vehicle } : entry),
    }), () => {
      const entry = this.state.entries.find((item) => item.vehicle.id === id);
      if (entry && entry.position) { this.drawMarker(entry.vehicle, entry.position, entry.stale); }
    });
  }

  /*
   * THE BACKEND'S CLOSED STATUS SET, IN THE RENDERER'S VOCABULARY.
   *
   * Only one word differs between the two, and translating it here rather than teaching the
   * renderer a second spelling keeps `stateLabel` the single place that decides what a reader
   * sees. Everything else -- `not_trackable`, `upstream_error`, `upstream_unavailable` --
   * passes through and lands on the renderer's catch-all, which is the correct sentence for
   * all three: the position is unavailable and waiting will not change it.
   */
  static stateFor(status) {
    return status === 'no_recent_position' ? 'no_recent' : status;
  }

  /**
   * ONE REQUEST FOR THE WHOLE FLEET, replacing the queue of one call per marker.
   *
   * The per-marker queue made the rate limit a cap on fleet size, and `provisioned` gated it
   * -- a client-side guess about which vehicles were worth asking about. The server now
   * answers for every visible vehicle with a status of its own, including for the ones the
   * client would have written off, so the guess no longer decides anything: it survives only
   * as the initial placeholder a real answer replaces.
   */
  async loadFleetPositions(current) {
    let items;
    try {
      items = await getJson(FLEET_POSITIONS_URL);
    } catch (err) {
      // A REFUSED FLEET READ IS REPORTED, NOT LEFT PENDING. Every vehicle would otherwise sit
      // on "locating..." for ever, which reads as progress rather than as a problem -- the
      // worst of the failure shapes, because nobody goes looking for it.
      this.safeSetState((prev) => ({
        status: 'error',
        message: this.props.t('couldNotLoadFleet'),
        entries: prev.entries.map((entry) => ({
          ...entry, position: null, stale: false, state: 'unavailable',
        })),
      }));
      return;
    }
    if (current && !current()) {
      return;   // a newer load has taken over; this answer is about a fleet we replaced
    }

    // KEYED BY VEHICLE ID, never zipped by index. The server does not promise an order, and
    // pairing two lists positionally would attach one truck's location to another -- worse
    // than an empty map, because it looks right.
    const answers = {};
    (items || []).forEach((item) => {
      if (item && item.vehicle) {
        answers[item.vehicle.id] = item;
      }
    });

    this.safeSetState(
      (prev) => ({
        entries: prev.entries.map((entry) => {
          const answer = answers[entry.vehicle.id];
          if (!answer) {
            // The contract is one item per visible vehicle, so this is the server breaking
            // it. Saying "unavailable" is honest; leaving the placeholder would promise a
            // position that is never coming.
            return { ...entry, position: null, stale: false, state: 'unavailable' };
          }
          return {
            ...entry,
            position: answer.position || null,
            stale: !!answer.stale,
            state: FleetMapView.stateFor(answer.status),
          };
        }),
      }),
      () => {
        this.state.entries.forEach((entry) => {
          if (entry.position) {
            this.drawMarker(entry.vehicle, entry.position, entry.stale);
          }
        });
      }
    );
  }

  /**
   * Vehicles the credentials report that this fleet does not hold.
   *
   * ADMIN-ONLY on the server, and a refusal here is NOT an error: for a driver or a manager
   * the honest answer is that there is nothing to show them. Failing the whole load over it
   * would blank a working map for everyone who is not an administrator.
   */
  async loadUnreconciled(current) {
    let items;
    try {
      items = await getJson(UNRECONCILED_URL);
    } catch (err) {
      this.safeSetState({ unreconciled: [] });
      return;
    }
    if (current && !current()) {
      return;
    }
    const unreconciled = (items || []).filter((item) => item && item.vin);
    this.safeSetState({ unreconciled }, () => {
      unreconciled.forEach((item) => {
        if (item.position) {
          // The VIN is the only name some of these have, so it is what rides on the pin.
          this.drawMarker(
            { id: 'unreconciled:' + item.vin, name: item.name, vin: item.vin },
            item.position,
            false,
            true
          );
        }
      });
    });
  }

  async initMap(apiKey) {
    await loadHereSdk();
    const H = window.H;
    if (!H || !this.mapRef.current || this.map) {
      return;
    }
    const platform = new H.service.Platform({ apiKey });
    const layers = platform.createDefaultLayers();
    this.map = new H.Map(this.mapRef.current, layers.vector.normal.map, {
      center: { lat: 62.5, lng: 25.5 }, // Finland, until the first marker arrives
      zoom: 5,
      pixelRatio: window.devicePixelRatio || 1,
    });
    new H.mapevents.Behavior(new H.mapevents.MapEvents(this.map));
    H.ui.UI.createDefault(this.map, layers);
    this.group = new H.map.Group();
    this.map.addObject(this.group);
    // CATCH UP on anything that arrived while the SDK was loading. Cheap insurance: on the
    // happy path there is nothing to redraw, and on the unhappy one it is the difference
    // between a marker and a silent omission.
    this.state.entries.forEach((entry) => {
      if (entry.position) {
        this.drawMarker(entry.vehicle, entry.position, entry.stale);
      }
    });
  }

  drawMarker(vehicle, position, stale, unreconciled) {
    const H = window.H;
    if (!H || !this.map || !this.group) {
      return;
    }
    const point = { lat: position.lat, lng: position.lng };
    const existing = this.markers[vehicle.id];
    if (existing) {
      this.group.removeObject(existing);
    }
    // A STALE FIX MUST NOT LOOK CURRENT, on a fleet map even more than on a single vehicle:
    // twelve identical pins invite the reader to assume twelve current positions. The colour
    // says how fresh, the label says which truck.
    const marker = new H.map.DomMarker(point, {
      icon: new H.map.DomIcon(
        pinMarkup(
          pinLabel(vehicle, this.props.t('vehicleFallback', { id: vehicle.id })),
          stale,
          unreconciled
        )
      ),
    });
    // A PIN IS A CONTROL, NOT A DECORATION. The single-vehicle screen already exists and is
    // routed; without this, a reader who spots the truck they want has no way to reach it and
    // has to go back and find the row. `data-vehicle-id` on the wrapper lets the delegated
    // handler work for markers drawn later, and survives HERE re-creating the DOM node.
    if (!unreconciled) {
      // NOTHING TO OPEN. An unreconciled truck has no Ovio vehicle to navigate to, and a pin
      // that answers a tap with a broken route is worse than one that does not answer.
      marker.addEventListener('tap', () => this.openVehicle(vehicle.id));
    }
    this.group.addObject(marker);
    this.markers[vehicle.id] = marker;

    // ONLY WHEN THE VIEW WOULD CHANGE. Re-framing on every arriving position made the camera
    // lurch once per vehicle -- fifteen times for a thirty-truck fleet, while nobody can read
    // a label or aim at a pin. The rule lives in `shouldReframe` and is tested there.
    if (shouldReframe(this.framedBox, point)) {
      this.frameFleet();
      this.framedBox = this.currentBox();
    }
  }

  /** The extent the camera is currently framing, as plain numbers, or null before layout. */
  currentBox() {
    const box = this.group && this.group.getBoundingBox();
    if (!box) {
      return null;
    }
    return {
      top: box.getTop(),
      left: box.getLeft(),
      bottom: box.getBottom(),
      right: box.getRight(),
    };
  }

  /** Open the single-vehicle screen for a pin or a row. */
  openVehicle(vehicleId) {
    if (this.props.history) {
      this.props.history.push('/fleet-location/' + vehicleId);
      return;
    }
    // Rendered outside a Route (the fleet page composes it directly), so there is no
    // injected history. A plain assignment reaches the same route through the router.
    window.location.assign('/fleet-location/' + vehicleId);
  }

  /*
   * FIT TO WHAT HAS ARRIVED, not to a guess. Positions arrive two at a time, so the view
   * tightens as the fleet fills in rather than starting zoomed on the first truck.
   *
   * The framing rule itself lives in `mapViewFor` and is tested there, because getting it
   * wrong is invisible from the code: fitting to the bounding box of ONE pin asks the
   * viewmodel to frame a box of zero area, and it answers with maximum zoom over a blank
   * tile. Every tenant on a real Scania credential holds exactly one located vehicle.
   */
  frameFleet() {
    const H = window.H;
    if (!H || !this.map) {
      return;
    }
    const points = Object.keys(this.markers).map((id) => {
      const geometry = this.markers[id].getGeometry();
      return { lat: geometry.lat, lng: geometry.lng };
    });
    const view = mapViewFor(points);
    if (view.kind === 'single') {
      this.map.getViewModel().setLookAtData({ position: view.point, zoom: view.zoom }, true);
      return;
    }
    if (view.kind === 'bounds') {
      // The padded rectangle from `mapViewFor`, not the group's own bounding box: fitting the
      // markers' exact extent puts the outermost pins on the map's edge, where the part drawn
      // above the anchor falls outside it.
      const box = view.bounds;
      this.map.getViewModel().setLookAtData(
        { bounds: new H.geo.Rect(box.top, box.left, box.bottom, box.right) },
        true
      );
    }
  }

  render() {
    const { status, hasKey, mapConfigured, entries, message, unreconciled } = this.state;
    const { t } = this.props;
    const located = entries.filter((entry) => entry.position);
    return (
      <CBMediaQuery>
        {(screenSize) => (
          <div style={getStyle(screenSize, styles, 'container')}>
            <h2 style={getStyle(screenSize, styles, 'heading')}>{t('fleetLocationTitle')}</h2>
            {status === 'loading' && (
              <p style={getStyle(screenSize, styles, 'summary')}>{t('loading')}</p>
            )}
            {(status === 'disabled' || status === 'error') && (
              <p style={getStyle(screenSize, styles, 'error')}>{message}</p>
            )}
            {status === 'ready' && entries.length === 0 && (
              <p style={getStyle(screenSize, styles, 'summary')}>{t('noVehiclesVisible')}</p>
            )}
            {status === 'ready' && entries.length > 0 && !hasKey && (
              <div style={getStyle(screenSize, styles, 'notice')}>
                <strong>
                  {mapConfigured ? t('noCredentialYetTitle') : t('mapNotConfiguredTitle')}
                </strong>{' '}
                {mapConfigured ? t('noCredentialYetBody') : t('mapNotConfiguredBody')}
              </div>
            )}
            {status === 'ready' && (
              <p style={getStyle(screenSize, styles, 'summary')}>
                {t('vehiclesLocated', { located: located.length, total: entries.length })}
              </p>
            )}
            <div
              ref={this.mapRef}
              // DECORATIVE TO A SCREEN READER, deliberately. The pins are unlabelled DOM
              // markup that announces nothing useful, and every fact they carry -- name,
              // VIN, status -- is in the list below, which is the accessible equivalent.
              // Declaring that beats leaving a reader to wander an unlabelled canvas.
              aria-hidden='true'
              style={{
                ...getStyle(screenSize, styles, 'map'),
                display: status === 'ready' && hasKey ? 'block' : 'none',
              }}
            />
                <FleetTable
              entries={entries}
              t={t}
              screenSize={screenSize}
              onOpen={(id) => this.openVehicle(id)}
              nameGeneration={this.generations.latest}
              onRename={(id, value, signal) => this.saveName(id, value, signal)}
            />
            <UnreconciledTable items={unreconciled} t={t} screenSize={screenSize} />
          </div>
        )}
      </CBMediaQuery>
    );
  }
}

/*
 * THE FLEET, IN WHICHEVER SHAPE THE SCREEN CAN HOLD.
 *
 * A phone gets cards; anything wider gets the table. Sixteen screens in this product already
 * make that switch, and the fleet map was rendering a four-column table into a 375-pixel
 * viewport -- readable only by scrolling sideways, which nobody does.
 *
 * Both shapes read from the same `stateLabel` and `cardFieldsFor`, so a truck cannot say one
 * thing on a laptop and another on a phone.
 */
function FleetTable({ entries, t, screenSize, onOpen, nameGeneration, onRename }) {
  if (entries.length === 0) {
    return null;
  }
  const renderName = (entry) => {
    const label = labelFor(entry.vehicle, t('vehicleFallback', { id: entry.vehicle.id }));
    const display = (
      <button type='button'
        onClick={(event) => {
          event.stopPropagation();
          onOpen(entry.vehicle.id);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            onOpen(entry.vehicle.id);
          }
        }}
        style={{ font: 'inherit', color: 'inherit', background: 'transparent',
          border: 0, padding: 0, textAlign: 'inherit', cursor: 'pointer' }}>
        {label}
      </button>
    );
    return entry.vehicle.can_rename === true ? (
      <InlineNameEditor key={nameGeneration + ':' + entry.vehicle.id} value={entry.vehicle.friendly_name}
        display={display} contextLabel={label} t={t} onSave={(value, signal) => onRename(entry.vehicle.id, value, signal)} />
    ) : display;
  };
  if (screenSize === 'small') {
    return (
      <div>
        {entries.map((entry) => {
          const label = labelFor(entry.vehicle, t('vehicleFallback', { id: entry.vehicle.id }));
          return (
          <div key={entry.vehicle.id}>
          <CBMobileCard
            title={label}
            titleControl={entry.vehicle.can_rename === true ? renderName(entry) : undefined}
            dialogTitle={label}
            subtitle={[entry.vehicle.manufacturer, entry.vehicle.model_name]
              .filter(Boolean)
              .join(' ')}
            fields={cardFieldsFor(entry, t)}
            // The face carries the two facts that fit; the dialog carries the rest, so a
            // phone loses nothing the desktop table shows.
            allFields={[
              { label: t('columnVehicle'), value: labelFor(entry.vehicle, '') },
              { label: t('columnVin'), value: entry.vehicle.vin },
              { label: t('columnStatus'), value: stateLabel(entry, t) },
            ]}
            onClick={() => onOpen(entry.vehicle.id)}
          />
          </div>
          );
        })}
      </div>
    );
  }
  return (
    <table style={getStyle(screenSize, styles, 'table')}>
      <thead>
        <tr>
          <th style={getStyle(screenSize, styles, 'tableHeader')}>{t('columnVehicle')}</th>
          <th style={getStyle(screenSize, styles, 'tableHeader')}>{t('columnVin')}</th>
          <th style={getStyle(screenSize, styles, 'tableHeader')}>{t('columnStatus')}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry) => {
          return (
          <tr
            key={entry.vehicle.id}
            onClick={() => onOpen(entry.vehicle.id)}
            style={{ cursor: 'pointer' }}
          >
            <td style={getStyle(screenSize, styles, 'tableCell')}>
              {renderName(entry)}
            </td>
            <td style={getStyle(screenSize, styles, 'tableCellMuted')}>{entry.vehicle.vin}</td>
            <td
              style={getStyle(
                screenSize,
                styles,
                entry.state === 'ok' && !entry.stale ? 'statusCurrent' : 'statusStale'
              )}
            >
              {stateLabel(entry, t)}
            </td>
          </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/*
 * VEHICLES THE CREDENTIALS REPORT AND THIS FLEET DOES NOT HOLD.
 *
 * Rendered BELOW the fleet and never mixed into it: these have no Ovio record, so there is no
 * registration to show and nothing to open. The heading says what they are rather than
 * leaving a reader to guess why an unfamiliar truck appears.
 *
 * Absent entirely when there are none, which is the normal state of a reconciled fleet.
 */
function UnreconciledTable({ items, t, screenSize }) {
  if (!items || items.length === 0) {
    return null;
  }
  return (
    <div style={{ padding: '8px 0' }}>
      <h3 style={getStyle(screenSize, styles, 'heading')}>{t('unreconciledTitle')}</h3>
      <p style={getStyle(screenSize, styles, 'summary')}>{t('unreconciledBody')}</p>
      <table style={getStyle(screenSize, styles, 'table')}>
        <thead>
          <tr>
            <th style={getStyle(screenSize, styles, 'tableHeader')}>{t('unreconciledColumnName')}</th>
            <th style={getStyle(screenSize, styles, 'tableHeader')}>{t('unreconciledColumnVin')}</th>
            <th style={getStyle(screenSize, styles, 'tableHeader')}>{t('unreconciledColumnModel')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.vin}>
              <td style={getStyle(screenSize, styles, 'tableCell')}>{item.name || '-'}</td>
              <td style={getStyle(screenSize, styles, 'tableCellMuted')}>{item.vin}</td>
              <td style={getStyle(screenSize, styles, 'tableCellMuted')}>
                {[item.brand, item.model].filter(Boolean).join(' ') || '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// withRef, because the page holds a ref to this component and calls `load()` on it when a
// credential is added. Without it the ref would point at the translation wrapper and the
// demo's whole point -- add a credential, watch the fleet appear -- would silently do nothing.
export default translate('FleetLocation', { withRef: true })(FleetMapView);
